import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";

export type AdminCalendarEvent = {
  id: string;
  title: string;
  date: string;
  time: string;
  dateLabel: string;
  location: string;
  description: string;
  link: string;
  startIso: string;
  allDay: boolean;
};

export type AdminCalendarSettings = {
  calendarApiUrl: string;
  publicCalendarUrl: string;
};

const SCHOOL_TIME_ZONE = "America/Chicago";
const fallbackCalendarApiUrl = asString(import.meta.env.VITE_CALENDAR_API_URL);

function asString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function toIso(value: unknown) {
  try {
    const maybeTimestamp = value as { toDate?: () => Date; toMillis?: () => number } | null | undefined;
    if (!maybeTimestamp) return "";
    if (typeof maybeTimestamp.toDate === "function") return maybeTimestamp.toDate().toISOString();
    if (typeof maybeTimestamp.toMillis === "function") return new Date(maybeTimestamp.toMillis()).toISOString();
    const parsed = new Date(String(value));
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : "";
  } catch {
    return "";
  }
}

function dateOnly(iso: string) {
  return iso ? iso.slice(0, 10) : "";
}

function parseCalendarApiDate(iso: string) {
  if (!iso) return null;
  const trimmed = iso.trim();
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const parsed = new Date(hasTimezone ? trimmed : `${trimmed}Z`);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function timeLabel(iso: string, allDay?: boolean) {
  if (allDay) return "All day";
  const parsed = parseCalendarApiDate(iso);
  if (!parsed) return "Time TBA";
  return parsed.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: SCHOOL_TIME_ZONE });
}

export function displayCalendarDate(value: string) {
  const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  const isoDate = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(value);
  const parsed = mmddyyyy
    ? new Date(Number(mmddyyyy[3]), Number(mmddyyyy[1]) - 1, Number(mmddyyyy[2]), 12)
    : isoDate
      ? new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]), 12)
      : new Date(`${value}T12:00:00`);
  if (!Number.isFinite(parsed.getTime())) return "Date TBA";
  return parsed.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function dashboardDateLabel(date: string) {
  const label = displayCalendarDate(date);
  return label === "Date TBA" ? label : label.replace(/^[A-Za-z]+,\s*/, "");
}

function mapCalendarApiItem(item: any): AdminCalendarEvent {
  const startIso = asString(item?.startIso);
  const allDay = item?.allDay === true;
  const date = asString(item?.startDate) || dateOnly(startIso);
  return {
    id: asString(item?.id || item?.uid || `${item?.title}-${startIso}`),
    title: asString(item?.title) || "School Event",
    date,
    time: asString(item?.timeLabel) || timeLabel(startIso, allDay),
    dateLabel: dashboardDateLabel(date),
    location: asString(item?.location),
    description: asString(item?.description),
    link: asString(item?.link),
    startIso,
    allDay,
  };
}

function mapFirestoreEvent(id: string, data: any): AdminCalendarEvent {
  const startIso = asString(data?.startIso) || toIso(data?.startAt || data?.start || data?.date);
  const allDay = data?.allDay === true;
  const date = asString(data?.startDate) || dateOnly(startIso);
  return {
    id,
    title: asString(data?.title) || "School Event",
    date,
    time: asString(data?.timeLabel || data?.startTime) || timeLabel(startIso, allDay),
    dateLabel: dashboardDateLabel(date),
    location: asString(data?.location),
    description: asString(data?.description),
    link: asString(data?.link || data?.url),
    startIso,
    allDay,
  };
}

export async function loadCalendarSettings(): Promise<AdminCalendarSettings> {
  const snap = await getDoc(doc(db, "settings", "calendar"));
  const data: any = snap.exists() ? snap.data() : {};
  return {
    calendarApiUrl: asString(data?.calendarApiUrl || data?.apiUrl) || fallbackCalendarApiUrl,
    publicCalendarUrl: asString(data?.publicCalendarUrl),
  };
}

async function loadApiEvents(calendarApiUrl: string, limitCount: number) {
  if (!calendarApiUrl) return [];
  const url = new URL(calendarApiUrl);
  url.searchParams.set("action", "calendarEvents");
  url.searchParams.set("limit", String(limitCount));
  url.searchParams.set("onlyFuture", "1");
  const response = await fetch(url.toString());
  const payload = await response.json();
  if (!payload?.ok || !Array.isArray(payload.items)) return [];
  return payload.items.map(mapCalendarApiItem);
}

async function loadFirestoreEvents(limitCount: number) {
  const snap = await getDocs(
    query(
      collection(db, "events"),
      where("startAt", ">=", new Date()),
      orderBy("startAt", "asc"),
      limit(limitCount)
    )
  );
  return snap.docs.map((item) => mapFirestoreEvent(item.id, item.data()));
}

export async function loadAdminCalendarEvents(limitCount = 60) {
  const settings = await loadCalendarSettings();
  let events: AdminCalendarEvent[] = [];

  try {
    events = await loadApiEvents(settings.calendarApiUrl, limitCount);
  } catch (error) {
    console.warn("[calendarFeed] Calendar API failed; trying Firestore events.", error);
  }

  if (!events.length) {
    events = await loadFirestoreEvents(limitCount).catch(() => []);
  }

  return { settings, events };
}
